import { useEffect, useState } from "react";
import { api, INR, compactINR } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/StatCard";
import { TierBadge } from "@/components/TierBadge";
import { Users, TrendingUp, UserPlus, Activity, CheckCircle2, PauseCircle, Percent, UserCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const ELEVATED = ["admin", "dept_lead"];
const TIER_COLORS = {
  Base: "#a1a1aa",
  Pro: "#3b82f6",
  "Pro Advanced": "#a855f7",
  Legend: "hsl(21 100% 50%)",
};

export default function Dashboard() {
  const { user } = useAuth();
  const canFilterByAsm = user && ELEVATED.includes(user.role);
  const [data, setData] = useState(null);
  const [asmId, setAsmId] = useState("");
  const [asms, setAsms] = useState([]);

  useEffect(() => {
    if (canFilterByAsm) api.get("/users").then((r) => setAsms(r.data.filter((u) => u.role === "asm")));
  }, [canFilterByAsm]);

  useEffect(() => {
    api.get("/dashboard", { params: asmId ? { asm_id: asmId } : {} }).then((r) => setData(r.data));
  }, [asmId]);

  if (!data) {
    return (
      <div className="p-10 text-muted-foreground">Loading dashboard…</div>
    );
  }

  const tierData = Object.entries(data.tier_distribution).map(([name, value]) => ({ name, value }));
  const k = data.kpis;

  return (
    <div>
      <PageHeader
        title="Dashboard"
        subtitle={`Overview for ${data.month}`}
        actions={
          canFilterByAsm && (
            <select
              data-testid="asm-filter"
              value={asmId}
              onChange={(e) => setAsmId(e.target.value)}
              className="h-10 rounded-full border border-border bg-card px-4 text-sm font-medium"
            >
              <option value="">All ASMs</option>
              {asms.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          )
        }
      />

      <div className="p-6 md:p-10 space-y-8">
        {/* KPIs — approved-only base for cohort/active metrics */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <StatCard testId="kpi-approved" label="Approved Affiliates" value={k.approved_affiliates} hint={`${k.total_affiliates} total (lifetime)`} icon={CheckCircle2} accent />
          <StatCard testId="kpi-paused" label="Paused Affiliates" value={k.paused_affiliates} hint="Currently paused" icon={PauseCircle} />
          <StatCard testId="kpi-joined" label="Joined This Month" value={k.joined_this_month} hint="New affiliates onboarded" icon={UserCheck} />
          <StatCard testId="kpi-new-cust" label="New Customers Acquired" value={k.new_customers} hint="This month" icon={UserPlus} />
          <StatCard testId="kpi-sales" label="Total Sales" value={compactINR(k.total_sales)} hint={INR(k.total_sales)} icon={TrendingUp} />
          <StatCard testId="kpi-cohort" label="Monthly Cohort %" value={`${k.monthly_cohort_pct}%`} hint={`${k.monthly_cohort_count} of ${k.approved_affiliates} approved sold this month`} icon={Percent} />
          <StatCard testId="kpi-active" label="Active Affiliates" value={k.active_affiliates} hint={`Of ${k.approved_affiliates} approved · lifetime sales`} icon={Users} />
          <StatCard testId="kpi-active-pct" label="Active Affiliates %" value={`${k.active_affiliates_pct}%`} hint="Approved with ≥1 lifetime order" icon={Activity} />
        </div>

        {/* Tier distribution — compact horizontal strip */}
        <div className="rounded-2xl border border-border/60 bg-card p-6" data-testid="tier-distribution">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold tracking-tight">Tier Distribution</h3>
            <span className="text-[11px] uppercase tracking-widest text-muted-foreground">{k.total_affiliates} affiliates</span>
          </div>
          {tierData.length === 0 ? (
            <div className="text-sm text-muted-foreground">No affiliates yet.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
              <div className="h-40 md:col-span-1" style={{ minHeight: 160 }}>
                <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={160}>
                  <PieChart>
                    <Pie data={tierData} innerRadius={45} outerRadius={70} dataKey="value" paddingAngle={2}>
                      {tierData.map((t) => (
                        <Cell key={t.name} fill={TIER_COLORS[t.name]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        background: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: 12,
                        fontSize: 12,
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="md:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {tierData.map((t) => {
                  const pct = k.total_affiliates ? Math.round((t.value / k.total_affiliates) * 100) : 0;
                  return (
                    <div key={t.name} className="rounded-xl bg-secondary/40 px-4 py-3">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: TIER_COLORS[t.name] }} />
                        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{t.name}</span>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-bold leading-none">{t.value}</span>
                        <span className="text-xs text-muted-foreground">{pct}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Top-10 leaderboards — two-column full-width */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top by Revenue */}
          <div className="rounded-2xl border border-border/60 bg-card p-6" data-testid="top-revenue-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold tracking-tight">Top by Revenue</h3>
              <span className="text-[11px] uppercase tracking-widest text-muted-foreground">This Month</span>
            </div>
            {data.top_by_revenue.length === 0 ? (
              <div className="text-sm text-muted-foreground py-8">No sales yet this month.</div>
            ) : (
              <div className="space-y-1">
                {data.top_by_revenue.map((a, i) => (
                  <Link
                    to={`/affiliates/${a.affiliate_id}`}
                    key={a.affiliate_id}
                    data-testid={`top-revenue-row-${i}`}
                    className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-secondary/60 transition-colors"
                  >
                    <div className="w-5 text-xs font-bold text-muted-foreground">{i + 1}</div>
                    {a.avatar_url ? (
                      <img src={a.avatar_url} alt="" className="h-8 w-8 rounded-full object-cover" />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold">{a.name.charAt(0)}</div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-semibold truncate">{a.name}</span>
                        <TierBadge tier={a.tier} />
                      </div>
                      <div className="text-[10px] text-muted-foreground">{a.new_customers} new · {a.commission_percent}%</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs font-bold">{INR(a.sales)}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Top by New Customers */}
          <div className="rounded-2xl border border-border/60 bg-card p-6" data-testid="top-new-cust-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold tracking-tight">Top by New Customers</h3>
              <span className="text-[11px] uppercase tracking-widest text-muted-foreground">This Month</span>
            </div>
            {data.top_by_new_customers.length === 0 ? (
              <div className="text-sm text-muted-foreground py-8">No new customers yet this month.</div>
            ) : (
              <div className="space-y-1">
                {data.top_by_new_customers.map((a, i) => (
                  <Link
                    to={`/affiliates/${a.affiliate_id}`}
                    key={a.affiliate_id}
                    data-testid={`top-new-cust-row-${i}`}
                    className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-secondary/60 transition-colors"
                  >
                    <div className="w-5 text-xs font-bold text-muted-foreground">{i + 1}</div>
                    {a.avatar_url ? (
                      <img src={a.avatar_url} alt="" className="h-8 w-8 rounded-full object-cover" />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold">{a.name.charAt(0)}</div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-semibold truncate">{a.name}</span>
                        <TierBadge tier={a.tier} />
                      </div>
                      <div className="text-[10px] text-muted-foreground">{INR(a.sales)} · {a.commission_percent}%</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm font-bold text-primary">{a.new_customers}</div>
                      <div className="text-[9px] uppercase tracking-widest text-muted-foreground">new</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
